/**
 * Validation for the commercial base records: clients, contacts, projects,
 * locations, and the laboratory's own numbering rules.
 *
 * THE GOVERNING IDEA IN THIS FILE IS RESTRAINT. The user is a reception desk
 * with a person standing in front of it. A form that refuses to accept a real
 * client because a field the laboratory does not have is marked required is a
 * worse failure than a duplicate somebody merges next week. So the only thing
 * required to create a client is a NAME, in at least one language — everything
 * else is optional here, and a laboratory that wants more adds it to its own
 * `clientRequiredFields` setting (decision D35).
 *
 * The backend re-validates all of this. The frontend uses the same schemas
 * purely so the desk hears about a problem before the round trip.
 */
import { z } from 'zod';
import { ALL_CLIENT_STATUSES, ALL_CLIENT_TYPES, ALL_NUMBERED_RECORD_TYPES, ALL_NUMBERING_RESET_POLICIES, ALL_PORTAL_VISIBILITIES, ALL_PROJECT_STATUSES, } from '@lims/shared-types';
import { businessTextSchema, optionalBusinessText, boundedText, emailSchema, isoDateSchema, listQuerySchema, phoneSchema, reasonSchema, referenceTextSchema, uuidSchema, } from './primitives.js';
// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------
export const clientStatusSchema = z.enum(ALL_CLIENT_STATUSES);
export const clientTypeSchema = z.enum(ALL_CLIENT_TYPES);
export const portalVisibilitySchema = z.enum(ALL_PORTAL_VISIBILITIES);
export const projectStatusSchema = z.enum(ALL_PROJECT_STATUSES);
export const numberedRecordTypeSchema = z.enum(ALL_NUMBERED_RECORD_TYPES);
export const numberingResetPolicySchema = z.enum(ALL_NUMBERING_RESET_POLICIES);
// ---------------------------------------------------------------------------
// Primitives specific to commercial records
// ---------------------------------------------------------------------------
/**
 * A VAT or commercial registration number as a person writes it (D206).
 *
 * Deliberately NOT a national format, and no longer a character set either:
 * this is a multi-tenant product sold to laboratories in more than one
 * country, and a number written with dots, a hash or brackets is still the
 * number its owner gave. It is user-managed reference data, stored as written;
 * only the generic limits of `referenceTextSchema` apply. A laboratory that
 * wants numbers checked against a national pattern gets that as
 * configuration, not as a constant.
 */
export const registrationNumberSchema = referenceTextSchema(50);
/**
 * Latitude and longitude for a project location.
 *
 * Six decimal places is roughly 0.1 m, which is far finer than a sampling
 * point is ever recorded to. Storing more would imply a precision nobody has.
 */
export const latitudeSchema = z.number().min(-90).max(90);
export const longitudeSchema = z.number().min(-180).max(180);
/** An optional free-text field: absent, or actual content. Never blank. */
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    // An empty string is an absent value, not a value of "". Storing "" would
    // make an empty field look like a recorded fact.
    .transform((value) => (value === '' ? null : value));
// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------
/**
 * Fields a laboratory may make mandatory for itself.
 *
 * `name` is absent from this list because it is mandatory for everyone: a
 * record with no name in either language is not a client, it is a blank row.
 */
export const CONFIGURABLE_REQUIRED_CLIENT_FIELDS = [
    'contactEmail',
    'contactPhone',
    'addressLine',
    'vatRegistrationNumber',
    'commercialRegistrationNumber',
    'clientType',
];
export const clientRequiredFieldsSchema = z
    .array(z.enum(CONFIGURABLE_REQUIRED_CLIENT_FIELDS))
    .max(CONFIGURABLE_REQUIRED_CLIENT_FIELDS.length);
/**
 * The identifying details duplicate detection compares.
 *
 * Every one is optional, including the name — a desk that only has a telephone
 * number should still be able to search on it.
 */
export const clientDuplicateQuerySchema = z
    .object({
    name: z.string().trim().max(500).optional(),
    contactEmail: z.string().trim().toLowerCase().max(320).optional(),
    contactPhone: z.string().trim().max(32).optional(),
    vatRegistrationNumber: z.string().trim().max(50).optional(),
    commercialRegistrationNumber: z.string().trim().max(50).optional(),
})
    .refine((value) => Object.values(value).some((field) => Boolean(field?.length)), { message: 'Provide at least one detail to search on' });
const clientCoreSchema = z.object({
    /** ONE name (D183). The registered legal name left the normal workflow. */
    name: businessTextSchema,
    clientType: clientTypeSchema.optional(),
    vatRegistrationNumber: registrationNumberSchema.nullable().optional(),
    commercialRegistrationNumber: registrationNumberSchema.nullable().optional(),
    contactEmail: emailSchema.nullable().optional(),
    contactPhone: phoneSchema.nullable().optional(),
    addressLine: optionalBusinessText(500),
    city: optionalBusinessText(200),
    /** ISO 3166-1 alpha-2. Optional, and never defaulted to the laboratory's
     *  own country — that would be an organisation-specific assumption. */
    countryCode: z
        .string()
        .trim()
        .toUpperCase()
        .length(2)
        .regex(/^[A-Z]{2}$/)
        .nullable()
        .optional(),
    notes: optionalText(4000),
});
export const createClientSchema = clientCoreSchema.extend({
    /**
     * Proof that a duplicate search was run before this form was filled in.
     *
     * OPTIONAL since the UX pass (D188): the separate search step is gone. The
     * server runs detection against the data actually submitted on every
     * create — that is the protection — and a token that is sent is still
     * verified (signed, short-lived, this laboratory's).
     */
    duplicateSearchToken: z.string().min(1).max(2048).optional(),
    /**
     * Existing clients the user looked at and decided are not this one.
     *
     * Named ids, not a blanket "yes". Acknowledging one near-duplicate does not
     * silently acknowledge a second one that appeared while the form was open.
     */
    acknowledgedDuplicateIds: z.array(uuidSchema).max(50).optional(),
    /** Why they are not the same client. Optional: sometimes the answer is
     *  simply "different company, same family name". */
    duplicateAcknowledgementNote: optionalText(1000),
});
export const updateClientSchema = clientCoreSchema.partial().extend({
    reason: reasonSchema.optional(),
});
export const suspendClientSchema = z.object({
    /**
     * Required, not optional. Suspension stops a laboratory dealing with a
     * client, and "why" is the first question anyone will ask afterwards.
     */
    reason: reasonSchema,
});
export const reinstateClientSchema = z.object({
    reason: reasonSchema.optional(),
});
export const listClientsQuerySchema = listQuerySchema.extend({
    status: clientStatusSchema.optional(),
    clientType: clientTypeSchema.optional(),
    /** Suspended clients are listed by default and clearly marked. Hiding them
     *  would make a laboratory's own history look shorter than it is. */
    includeSuspended: z
        .enum(['true', 'false'])
        .default('true')
        .transform((value) => value === 'true'),
});
// ---------------------------------------------------------------------------
// Client contacts
// ---------------------------------------------------------------------------
const contactCoreSchema = z.object({
    name: businessTextSchema,
    position: optionalBusinessText(200),
    email: emailSchema.nullable().optional(),
    phone: phoneSchema.nullable().optional(),
    mobile: phoneSchema.nullable().optional(),
    /** At most one per client. Setting a new one clears the old. */
    isPrimary: z.boolean().optional(),
    notes: optionalText(2000),
});
/**
 * Portal eligibility. Changing either field requires `portal_accounts.manage`
 * on top of `clients.update`: recording a contact's telephone number and
 * deciding who may see the laboratory's reports are different acts.
 *
 * The portal is Phase 11. Setting these creates no account and grants no
 * access today (decision D38).
 */
export const contactPortalSchema = z.object({
    portalEnabled: z.boolean(),
    portalVisibility: portalVisibilitySchema,
});
/**
 * Enabling a contact for the portal WITHOUT naming a visibility is allowed
 * since Phase 11: the backend applies the laboratory's
 * `portal_default_visibility` (D146). Naming `none` while enabling is still a
 * contradiction — access to nothing — and is refused here as well as by the
 * API and the database.
 */
const portalPairIsCoherent = (value) => !(value.portalEnabled === true && value.portalVisibility === 'none');
const PORTAL_PAIR_REFINEMENT = {
    message: 'A contact with portal access must be given something to see',
    path: ['portalVisibility'],
};
export const createClientContactSchema = contactCoreSchema
    .extend({
    portalEnabled: z.boolean().optional(),
    portalVisibility: portalVisibilitySchema.optional(),
})
    .refine(portalPairIsCoherent, PORTAL_PAIR_REFINEMENT);
export const updateClientContactSchema = contactCoreSchema
    .partial()
    .extend({
    portalEnabled: z.boolean().optional(),
    portalVisibility: portalVisibilitySchema.optional(),
    /** Contacts are deactivated, never deleted: a report was sent to this
     *  person, and that stays true after they leave the company. */
    isActive: z.boolean().optional(),
    reason: reasonSchema.optional(),
})
    .refine(portalPairIsCoherent, PORTAL_PAIR_REFINEMENT);
// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------
const projectCoreSchema = z.object({
    name: businessTextSchema,
    /**
     * The CLIENT'S own reference for the project, if they have one. Not a
     * generated number: the laboratory does not number projects, and inventing a
     * numbering type nobody asked for would be a silent scope change.
     */
    reference: optionalText(64),
    description: optionalBusinessText(2000),
    /**
     * The three party fields, all OPTIONAL and deliberately so. A laboratory
     * often knows only who is paying. Forcing a consultant's name would mean
     * inventing one (PLAN.md, Phase 4 acceptance criteria). Empty is `null`.
     */
    contractorName: optionalBusinessText(500),
    consultantName: optionalBusinessText(500),
    ownerName: optionalBusinessText(500),
    startDate: isoDateSchema.nullable().optional(),
    expectedEndDate: isoDateSchema.nullable().optional(),
    notes: optionalText(4000),
});
export const createProjectSchema = projectCoreSchema.extend({
    clientId: uuidSchema,
});
export const updateProjectSchema = projectCoreSchema.partial().extend({
    status: projectStatusSchema.optional(),
    reason: reasonSchema.optional(),
});
export const listProjectsQuerySchema = listQuerySchema.extend({
    clientId: uuidSchema.optional(),
    status: projectStatusSchema.optional(),
});
// ---------------------------------------------------------------------------
// Project locations
// ---------------------------------------------------------------------------
const locationCoreSchema = z.object({
    name: businessTextSchema,
    description: optionalBusinessText(2000),
    addressLine: optionalBusinessText(500),
    city: optionalBusinessText(200),
    latitude: latitudeSchema.nullable().optional(),
    longitude: longitudeSchema.nullable().optional(),
    notes: optionalText(2000),
});
/**
 * A coordinate is a PAIR. One half of it locates nothing, and storing a
 * latitude with no longitude would put a point on a map that means nothing.
 */
const coordinatesArePaired = (value) => (value.latitude === undefined || value.latitude === null) ===
    (value.longitude === undefined || value.longitude === null);
export const createProjectLocationSchema = locationCoreSchema.refine(coordinatesArePaired, {
    message: 'Latitude and longitude must be given together',
    path: ['longitude'],
});
export const updateProjectLocationSchema = locationCoreSchema
    .partial()
    .extend({
    isActive: z.boolean().optional(),
    reason: reasonSchema.optional(),
})
    .refine(coordinatesArePaired, {
    message: 'Latitude and longitude must be given together',
    path: ['longitude'],
});
// ---------------------------------------------------------------------------
// Numbering rules
// ---------------------------------------------------------------------------
/**
 * A laboratory's numbering rule for one record type.
 *
 * The format/reset pairing is checked HERE only for shape. The semantic check
 * — that a yearly reset carries a year token — is `validateNumberingFormat`,
 * applied by the API and again by a database CHECK.
 */
export const updateNumberingRuleSchema = z.object({
    format: boundedText(1, 64),
    resetPolicy: numberingResetPolicySchema,
});
export const numberingPreviewQuerySchema = z.object({
    format: boundedText(1, 64),
    resetPolicy: numberingResetPolicySchema,
});
//# sourceMappingURL=commercial.js.map